import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useEntidad, useManagedContext } from "../../components/hooks"
import { ServerRequest } from "../../utils/apiweb"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis"
import { SectionHeading, TableCustom, WrapperAdvancedSearch } from "../../components/common"
import { onRequestError, onRequestNoSuccess, onSearchNoSuccess } from "../../utils/requests"
import { VALIDATION_MESSAGE } from "../../consts/validationMessage"
import { getDateToString, getFormatNumber, getDateSerialize } from "../../utils/convert"
import PublicacionesGridModal from "../../components/controls/PublicacionesGridModal"
import { useReporte } from "../../components/hooks/useReporte"
import { OpenObjectURL } from "../../utils/helpers"
import { isValidDate } from "../../utils/validator"

const initItem = {
    id: 0,
    importeTotal: 0
}

const PublicacionHistorialView = () => {
    const { setIsLoading, showMessageModal } = useManagedContext()
    const location = useLocation()
    const navigate = useNavigate()

    const volverAlVep = location.state?.volverAlVep

    const searchRef = useRef()

    const [lotes, setLotes] = useState([])
    const [tableVisible, setTableVisible] = useState(false)
    const [searchMessage, setSearchMessage] = useState(null)
    const [editItem, setEditItem] = useState(initItem)
    const [showDetalle, setShowDetalle] = useState(false)

    const [, getRowEntidad ] = useEntidad({
        entidades: ['Recaudadora'],
        onLoaded: (_, isSuccess, error) => {
          if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        },
        memo: {
          key: 'Recaudadora',
          timeout: 0
        }
    })

    useEffect(() => { SearchLotes() }, [])

    const [ generateReporte, ] = useReporte({
        callback: (reporte, buffer, message) => {
            if (buffer) {
                OpenObjectURL(`${reporte}.pdf`, buffer);
            }
            else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message)
            }
            setIsLoading(false)
        },
        mode: 'blob'
    })

    const SearchLotes = () => {
        const filters = searchRef.current.filters
        let resolveSearch
        const searchDone = new Promise((resolve) => { resolveSearch = resolve })

        setIsLoading(true)

        setSearchMessage(null)

        const paramsUrl = `/filter?`
            + `numeroLote=${encodeURIComponent(filters.numeroLote)}`
            + `&fechaDesde=${encodeURIComponent(getDateSerialize(filters.fechaLoteDesde))}`
            + `&fechaHasta=${encodeURIComponent(getDateSerialize(filters.fechaLoteHasta))}`

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.RECIBO_PUBLICACION_LOTE,
            paramsUrl,
            null,
            res => res.json().then(data => {
                data.forEach(row => {
                    if (row.fechaLote) row.fechaLote = new Date(row.fechaLote)
                })
                data.sort((a, b) => b.id - a.id)
                setLotes(data)
                setTableVisible(true)
                setIsLoading(false)
                resolveSearch(true)
            }),
            res => {
                onSearchNoSuccess(res, () => {
                    setLotes([])
                    setTableVisible(false)
                    setSearchMessage(VALIDATION_MESSAGE.TOO_MANY_RESULTS)
                })
                setIsLoading(false)
                resolveSearch(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
                resolveSearch(false)
            },
        )

        return searchDone
    }

    const confirmRemoveId = (id) => {
        setIsLoading(true)

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.RECIBO_PUBLICACION_LOTE,
            `/${id}`,
            null,
            () => {
                setLotes(prev => prev.filter(x => x.id !== id))
                setIsLoading(false)
            },
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
            },
        )
    }

    const emitirInformeRecaudacionLote = (id) => {
        setIsLoading(true)
        const paramsReporte = {
            idRecaudacionLote: id
        }
        generateReporte("InformeRecaudacionLote", paramsReporte);
    }

    const avmr = {
        onView: (item) => {
            setEditItem(item)
            setShowDetalle(true)
        },
        onDownload: (item) => {  
            if (!isValidDate(item.fechaControl, true)) return ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'El lote debe estar controlado antes de emitir el informe')
            emitirInformeRecaudacionLote(item.id)
        },
        onRemove: ({ id }) => {
            showMessageModal({
                title: "Confirmación",
                message: "¿Está seguro de borrar el lote?",
                onConfirm: () => confirmRemoveId(id),
            })
        }
    }


    const onModalCancel = () => {
        setShowDetalle(false)
        setEditItem(initItem)
    }


    return <>
        <SectionHeading titles={[{ title: 'VEPs' }, { title: 'Historial de VEPs' }]} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                message={searchMessage}
                ref={searchRef}
                filters={[
                    { title: 'N° lote', field: 'numeroLote', type: 'string', size: 4, searchOnEnter: true, presetValue: location.state?.numeroLote },
                    { title: 'Fecha desde', field: 'fechaLoteDesde', type: 'date', size: 4 },
                    { title: 'Fecha hasta', field: 'fechaLoteHasta', type: 'date', size: 4, minValueField: 'fechaLoteDesde' },
                ]}
                onSearch={SearchLotes}
            />

            {tableVisible && (
                <TableCustom
                    className={'TableCustomBase m-top-20'}
                    data={lotes}
                    avmr={avmr}
                    columns={[
                        { Header: 'N° lote', accessor: 'numeroLote', width: '30%', },
                        { Header: 'Fecha lote', accessor: 'fechaLote', Cell: (data) => getDateToString(data.value), width: '10%'},
                        { Header: 'Casos', accessor: 'casos', Cell: (data) => getFormatNumber(data.value, 0), width: '7%', alignCell: 'right' },
                        { Header: 'Casos agregados', accessor: 'casosAgregados', Cell: (data) => getFormatNumber(data.value, 0), width: '15%', alignCell: 'right' },
                        { Header: 'Importe total 1', accessor: 'importeTotal1', Cell: (data) => getFormatNumber(data.value, 2), width: '19%', alignCell: 'right' },
                        { Header: 'Importe total 2', accessor: 'importeTotal2', Cell: (data) => getFormatNumber(data.value, 2), width: '19%', alignCell: 'right' },
                    ]}
                />
            )}

            {showDetalle && (
                <PublicacionesGridModal
                    idPublicacionLote={editItem.id}
                    onCancel={onModalCancel}
                />
            )}

        </section>

        {volverAlVep && (
            <footer className='footer footer-action'>
                <div className='footer-action-container'>
                    <button className="btn back-button float-start" onClick={() => navigate(volverAlVep.to, { state: volverAlVep.state })}>Volver al VEP</button>
                </div>
            </footer>
        )}
    </>
}

export default PublicacionHistorialView
